import { Capacitor } from '@capacitor/core';

/**
 * APNs-Push-Anbindung der nativen iOS-Shell (P4) — Plugin-Seite.
 *
 * Nur aus isNativeApp()-Zweigen aufrufen (usePushSubscription,
 * NativePushNavigation). HTTP zur API macht der Aufrufer — hier lebt
 * ausschließlich die Plugin-Interaktion, dynamisch importiert, damit
 * das Web-Bundle das Plugin-JS nur im nativen Fall lädt.
 *
 * Drift-Regel (CLAUDE.md): vor jeder Nutzung isPluginAvailable prüfen —
 * eine ältere Shell ohne PushNotifications-Plugin darf nicht brechen.
 */

export function nativePushAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PushNotifications');
}

export async function checkNativePushPermission(): Promise<'granted' | 'denied' | 'prompt'> {
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const status = await PushNotifications.checkPermissions();
  if (status.receive === 'granted') return 'granted';
  if (status.receive === 'denied') return 'denied';
  return 'prompt';
}

/**
 * Permission erfragen (braucht User-Geste) und APNs-Device-Token holen.
 * Wirft bei Ablehnung oder wenn iOS nicht binnen Timeout registriert.
 */
export async function getNativePushToken(timeoutMs = 10000): Promise<string> {
  const { PushNotifications } = await import('@capacitor/push-notifications');

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') {
    throw new Error('Benachrichtigungen wurden nicht erlaubt.');
  }

  // register() liefert den Token asynchron über das 'registration'-Event.
  // Listener-Handles einsammeln und in finally entfernen — sonst
  // akkumuliert jeder Toggle-Klick weitere Listener (KEIN
  // removeAllListeners: das würde die Tap-Navigation mitkillen).
  const handles: Array<{ remove: () => Promise<void> }> = [];
  try {
    return await new Promise<string>((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new Error('Push-Registrierung hat zu lange gedauert.')),
        timeoutMs,
      );
      void PushNotifications.addListener('registration', (t) => {
        clearTimeout(timer);
        resolve(t.value);
      }).then((h) => handles.push(h));
      void PushNotifications.addListener('registrationError', (e) => {
        clearTimeout(timer);
        reject(new Error(`Push-Registrierung fehlgeschlagen: ${JSON.stringify(e)}`));
      }).then((h) => handles.push(h));
      void PushNotifications.register().catch((e: unknown) => {
        clearTimeout(timer);
        reject(e instanceof Error ? e : new Error(String(e)));
      });
    });
  } finally {
    for (const h of handles) void h.remove().catch(() => undefined);
  }
}

/** Best effort — der Server-Delete ist die verbindliche Abmeldung. */
export async function unregisterNativePush(): Promise<void> {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.unregister();
  } catch {
    /* alte Shell ohne unregister o. ä. — bewusst ignorieren */
  }
}

/**
 * Tap auf eine Notification → In-App-Navigation auf payload.url
 * (Payload-Vertrag mit api/_lib/apns.ts: Custom-Key `url`).
 */
export async function listenForPushNavigation(
  onNavigate: (url: string) => void,
): Promise<() => void> {
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const handle = await PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action) => {
      const url = (action.notification.data as { url?: string } | undefined)?.url;
      if (typeof url === 'string' && url.startsWith('/')) onNavigate(url);
    },
  );
  return () => void handle.remove();
}
